import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Box,
  Container,
  Typography,
  Paper,
  InputBase,
  Button,
  TextField,
  Popper,
  ClickAwayListener,
  Autocomplete,
} from "@mui/material";

import { DatePicker } from "@mui/x-date-pickers";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PublicIcon from "@mui/icons-material/Public";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import SearchIcon from "@mui/icons-material/Search";
import dayjs from "dayjs";
import TravelersModal from "./Modals/TravellerModal";
import { debounce } from "lodash";
import { searchAPI } from "../API/Seach";
import Loader from "./Loader";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';
import { Skeleton, Fade, Slide } from "@mui/material";
import { tokens } from "../theme/tokens";
import { COUNTRIES, DEFAULT_SEARCH_COUNTRY } from "../data/countries";
import { buildOccupanciesFromTravelers } from "../Utils/occupancies";

/** Plain text sent as searchString (e.g. "goa india"); axios encodes spaces as %20 in query params. */
function buildLocationSearchString(text, country) {
  const t = (text || "").trim();
  const c = (country || "").trim();
  if (!c) return t;
  return t ? `${t} ${c}` : c;
}

const SearchSection = () => {
  const [checkIn, setCheckIn] = useState(dayjs());
  const [checkOut, setCheckOut] = useState(dayjs().add(1, "day"));
  const [searchText, setSearchText] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [openTravelersModal, setOpenTravelersModal] = useState(false);
  const [travelers, setTravelers] = useState({
    adults: 2,
    children: 0,
    rooms: 1,
    childAges: []
  });

  const [selectedLocation, setSelectedLocation] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const userId = parseInt(localStorage.getItem("userId"), 10);
  const [noDataFound, setNoDataFound] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState(DEFAULT_SEARCH_COUNTRY);
  const selectedCountryRef = useRef(DEFAULT_SEARCH_COUNTRY);

  const getLocationPopupWidth = () => {
    if (!anchorEl) return undefined;
    const anchorWidth = Number(anchorEl?.offsetWidth) || 0;
    const viewportWidth = typeof window !== "undefined" ? window.innerWidth : anchorWidth;
    // Keep dropdown comfortably wider than the field on desktop, but safe on small screens.
    const preferred = Math.max(anchorWidth, 420);
    return Math.min(preferred, Math.max(280, viewportWidth - 24));
  };

  useEffect(() => {
    selectedCountryRef.current = selectedCountry;
  }, [selectedCountry]);

  /** Aligns with MUI outlined fields + labels; Search button stretches to same row height */
  const fieldHeight = 56;

  const datePickerSx = {
    width: "100%",
    flex: "0 0 auto",
    minWidth: 0,
    "& .MuiOutlinedInput-root": {
      borderRadius: `${tokens.radiusMd}px`,
      height: fieldHeight,
      backgroundColor: tokens.paper,
      "&:hover": {
        "& .MuiOutlinedInput-notchedOutline": {
          borderColor: tokens.borderStrong,
        },
      },
    },
    "& .MuiInputLabel-root": {
      color: tokens.textMuted,
      fontSize: "0.8rem",
      "&.Mui-focused": {
        color: tokens.accent,
      },
    },
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: tokens.border,
    },
    "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: `${tokens.accent} !important`,
    },
    "& .MuiInputBase-input": {
      fontSize: "0.8125rem",
      fontWeight: 500,
      py: 0.75,
    },
  };
const LoadingSkeletons = () => (
  <Box sx={{ p: 1 }}>
    {[1, 2, 3].map((item, index) => (
      <Slide
        key={item}
        direction="down"
        in={true}
        timeout={300}
        style={{ transitionDelay: `${index * 100}ms` }}
      >
        <Box
          sx={{
            p: 2,
            borderBottom: "1px solid #eee",
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <LocationOnIcon sx={{ color: "#666", fontSize: "1.2rem" }} />
          <Box sx={{ flex: 1 }}>
            <Skeleton 
              variant="text" 
              width="70%" 
              height={24}
              sx={{ 
                bgcolor: 'rgba(0,0,0,0.06)',
                borderRadius: '4px',
                mb: 0.5
              }}
            />
            <Skeleton 
              variant="text" 
              width="50%" 
              height={20}
              sx={{ 
                bgcolor: 'rgba(0,0,0,0.04)',
                borderRadius: '4px'
              }}
            />
          </Box>
        </Box>
      </Slide>
    ))}
  </Box>
);
const NoResultsFound = ({ searchQuery }) => (
  <Slide
    direction="down"
    in={true}
    timeout={300}
  >
    <Box
      sx={{
        p: 3,
        textAlign: "center",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 1,
      }}
    >
      <Box
        sx={{
          width: 48,
          height: 48,
          borderRadius: "50%",
          backgroundColor: "rgba(0,0,0,0.04)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mb: 1,
        }}
      >
        <Typography sx={{ fontSize: "1.5rem" }}>🔍</Typography>
      </Box>
      <Typography
        variant="body1"
        sx={{ 
          fontWeight: 500,
          color: "#333",
          fontSize: "0.95rem"
        }}
      >
        No results found
      </Typography>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ fontSize: "0.82rem", maxWidth: "250px" }}
      >
        We couldn't find any locations matching "{searchQuery}". Try searching for a city, hotel, or landmark.
      </Typography>
    </Box>
  </Slide>
);

const SearchResultItem = ({ result, onClick, index }) => (
  <Slide
    direction="down"
    in={true}
    timeout={300}
    style={{ transitionDelay: `${index * 50}ms` }}
  >
    <Box
      onClick={() => onClick(result)}
      sx={{
        p: 2,
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        gap: 1,
        "&:hover": {
          backgroundColor: tokens.accentSoft,
        },
        borderBottom: `1px solid ${tokens.border}`,
        transition: "all 0.2s ease",
        "&:last-child": {
          borderBottom: "none",
        },
      }}
    >
      <LocationOnIcon sx={{ color: tokens.accent, fontSize: "1.2rem" }} />
      <Box sx={{ flex: 1 }}>
        <Typography
          variant="body1"
          sx={{ 
            fontWeight: 500,
            color: "#333",
            fontSize: "0.95rem"
          }}
        >
          {result.name}
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ fontSize: "0.82rem" }}
        >
          {`${result.city ? result.city + ', ' : ''}${result.state ? result.state + ', ' : ''}${result.country}`}
        </Typography>
      </Box>
    </Box>
  </Slide>
);

  const handleTravelersChange = (newTravelers) => {
    setTravelers({
      ...newTravelers,
      childAges: newTravelers.childAges || []
    });
  };

  const handleSearch = useMemo(
    () =>
      debounce(async (text) => {
        const trimmed = (text || "").trim();
        const country = (selectedCountryRef.current || "").trim();
        const searchString = buildLocationSearchString(trimmed, country);

        if (trimmed.length >= 3) {
          setLoading(true);
          setNoDataFound(false);
          try {
            const response = await searchAPI.searchLocation(searchString);
            const locations = response?.data?.locations || [];

            if (response.status === "success" && response.statusCode === 200) {
              if (locations.length > 0) {
                setSearchResults(locations);
                setNoDataFound(false);
              } else {
                setSearchResults([]);
                setNoDataFound(true);
              }
            } else {
              setSearchResults([]);
              setNoDataFound(true);
            }
          } catch (error) {
            console.error("Search failed:", error);
            setSearchResults([]);
            setNoDataFound(true);
          } finally {
            setLoading(false);
          }
        } else {
          setSearchResults([]);
          setNoDataFound(false);
        }
      }, 300),
    []
  );

  useEffect(() => {
    return () => handleSearch.cancel();
  }, [handleSearch]);

  /** Re-run location search when country changes and query is long enough */
  useEffect(() => {
    const trimmed = searchText.trim();
    if (trimmed.length >= 3) {
      handleSearch.cancel();
      handleSearch(searchText);
    }
  }, [selectedCountry]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLocationSelect = (location) => {
    setSearchText(location.fullName);
    setSelectedLocation(location);
    setSearchResults([]);
    setAnchorEl(null);
  };

  const handleSearchHotels = async () => {
    if (!selectedLocation) {
      toast.warning("Please select a location first");
      return;
    }
    if (!checkIn || !checkOut) {
      toast.warning("Please select check-in and check-out dates");
      return;
    }

    setIsSearching(true);

    const occupancies = buildOccupanciesFromTravelers(travelers);

    const searchParams = {
      UserId: userId,
      locationId: selectedLocation.type !== "Hotel" ? parseInt(selectedLocation.id) : 0,
      hotelId: selectedLocation.type === "Hotel" ? [selectedLocation.id.toString()] : [],      
      checkIn: checkIn.format("YYYY-MM-DD"),
      checkOut: checkOut.format("YYYY-MM-DD"),
      occupancies: occupancies,
      filterBy: {
        ratings: [],
        freeBreakfast: false,
        isRefundable: false,
        subLocationIds: [],
        facilities: [],
        type: null,
        tags: [],
        reviewRatings: [],
        minPrice: 0,
        maxPrice: 0
      },
      page: 1,
      traceId: null,
    };

    try {
      const response = await searchAPI.searchHotels(searchParams);

      if (response.status === "success" && response.statusCode === 200) {
        navigate("/search", {
          state: {
            // pass full data object: { query, totalPages, currentPage, totalCount, data: [...] }
            searchResults: response.data,
            searchParams: {
              location: selectedLocation,
              checkIn: checkIn.format("YYYY-MM-DD"),
              checkOut: checkOut.format("YYYY-MM-DD"),
              travelers: travelers,
            },
          },
        });
      } else {
        throw new Error(response.message || "Search failed");
      }
    } catch (error) {
      console.error("Failed to search hotels:", error);
      toast.error("Failed to search hotels. Please try again");
    } finally {
      setIsSearching(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setInitialLoading(false);
    }, 2000);

    return () => clearTimeout(timer);
  }, []);

  return (
    <>
      <ToastContainer position="top-right" autoClose={3000} />
      {(initialLoading || isSearching) && <Loader />}
      <Box
        sx={{
          backgroundImage: `linear-gradient(115deg, rgba(10, 30, 64, 0.88) 0%, rgba(10, 30, 64, 0.55) 45%, rgba(45, 106, 143, 0.45) 100%), url(https://images.unsplash.com/photo-1566073771259-6a8506099945?auto=format&fit=crop&w=2400&q=80)`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          minHeight: { xs: 560, md: 620 },
          display: "flex",
          alignItems: "center",
          position: "relative",
          paddingTop: { xs: "120px", md: "132px" },
          paddingBottom: { xs: 5, md: 7 },
          zIndex: 1,
        }}
      >
        <Container
          maxWidth={false}
          sx={{
            position: "relative",
            zIndex: 1,
            px: { xs: 2, md: 3 },
            maxWidth: { xs: "100%", md: "min(100%, 1260px)" },
            mx: "auto",
          }}
        >
          <Typography
            component="h1"
            variant="h2"
            sx={{
              color: "#fff",
              fontWeight: 600,
              textAlign: "center",
              mb: 2,
              fontSize: { xs: "1.85rem", sm: "2.35rem", md: "3.25rem" },
              fontFamily: tokens.fontDisplay,
              letterSpacing: "-0.02em",
              lineHeight: 1.15,
              maxWidth: 920,
              mx: "auto",
              textShadow: "0 2px 24px rgba(0,0,0,0.35)",
              "& sup": {
                fontSize: "0.55em",
                top: "-0.35em",
                fontWeight: 500,
              },
            }}
          >
            Your perfect stay, simplified<sup>™</sup>
          </Typography>
          <Typography
            variant="h6"
            sx={{
              color: "rgba(255, 255, 255, 0.92)",
              fontWeight: 500,
              textAlign: "center",
              mb: 4,
              fontSize: { xs: "0.95rem", sm: "1.05rem", md: "1.15rem" },
              lineHeight: 1.65,
              maxWidth: 640,
              mx: "auto",
              textShadow: "0 1px 12px rgba(0,0,0,0.25)",
            }}
          >
            Search negotiated B2B rates, compare properties, and book with confidence—one consistent
            workflow for your teams.
          </Typography>

          <Paper
            elevation={0}
            sx={{
              width: "100%",
              maxWidth: { xs: "100%", md: "min(100%, 1190px)" },
              borderRadius: `${tokens.radiusLg}px`,
              minHeight: { xs: "auto", md: 72 },
              margin: "0 auto",
              overflow: "visible",
              backgroundColor: tokens.paper,
              border: `1px solid ${tokens.border}`,
              boxShadow: tokens.shadowLg,
              p: { xs: 1.25, md: 1.25 },
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                flexWrap: { md: "nowrap" },
                gap: { xs: 1.75, md: 1.6 },
                alignItems: "stretch",
                justifyContent: { md: "flex-start" },
                p: { xs: 0.75, md: 1.5 },
                minWidth: 0,
                /* Keeps the last control from spilling past the card edge */
                boxSizing: "border-box",
              }}
            >
              {/* Country — narrows location search (e.g. "goa" + India → API searchString "goa India") */}
              <Autocomplete
                options={COUNTRIES}
                value={selectedCountry || null}
                onChange={(_, newValue) => setSelectedCountry(newValue || "")}
                isOptionEqualToValue={(option, value) => option === value}
                clearOnEscape
                blurOnSelect
                slotProps={{
                  popper: {
                    sx: {
                      zIndex: 1400,
                    },
                  },
                }}
                ListboxProps={{
                  sx: {
                    maxHeight: 280,
                    "& .MuiAutocomplete-option": {
                      fontSize: "0.9rem",
                      py: 1,
                    },
                  },
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Country"
                    placeholder="Search or select"
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <Box
                            component="span"
                            sx={{
                              display: "flex",
                              pl: 0.5,
                              pr: 0.25,
                              color: tokens.textMuted,
                              alignItems: "center",
                            }}
                          >
                            <PublicIcon sx={{ fontSize: "1.15rem" }} />
                          </Box>
                          {params.InputProps.startAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                sx={{
                  width: { xs: "100%", md: 200 },
                  minWidth: 0,
                  flexShrink: 0,
                  alignSelf: "stretch",
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "flex-end",
                  "& .MuiOutlinedInput-root": {
                    height: "100%",
                    minHeight: fieldHeight,
                    borderRadius: `${tokens.radiusMd}px`,
                    backgroundColor: tokens.paper,
                    "& fieldset": { borderColor: tokens.border },
                    "&:hover fieldset": { borderColor: tokens.borderStrong },
                    "&.Mui-focused fieldset": { borderColor: tokens.accent },
                    "&.Mui-focused": {
                      boxShadow: `0 0 0 3px ${tokens.accentSoft}`,
                    },
                  },
                  "& .MuiInputLabel-root": {
                    color: tokens.textMuted,
                    fontSize: "0.8rem",
                    "&.Mui-focused": { color: tokens.accent },
                  },
                  "& .MuiInputBase-input": {
                    fontSize: "0.8125rem",
                    fontWeight: 500,
                  },
                }}
              />

              {/* Where to? Field */}
       <ClickAwayListener onClickAway={() => setAnchorEl(null)}>
         <Box
           sx={{
             display: "flex",
             alignItems: "center",
             /* Fixed width on desktop — do not flex-grow or it steals the whole row */
             flex: { xs: "1 1 auto", md: "0 0 228px" },
             minWidth: 0,
             alignSelf: "stretch",
             p: {
               xs: "8px 12px",
               sm: "0 12px",
             },
             border: `1px solid ${tokens.border}`,
             borderRadius: `${tokens.radiusMd}px`,
             backgroundColor: tokens.paper,
             width: { xs: "100%", md: 228 },
             maxWidth: { xs: "100%", md: 260 },
             minHeight: fieldHeight,
             "&:hover": {
               borderColor: tokens.borderStrong,
             },
             "&:focus-within": {
               borderColor: tokens.accent,
               boxShadow: `0 0 0 3px ${tokens.accentSoft}`,
             },
           }}
         >
           <LocationOnIcon sx={{ color: tokens.textMuted, fontSize: "1.1rem" }} />
           <InputBase
             placeholder="Where to?"
             value={searchText}
             onChange={(e) => {
               setSearchText(e.target.value);
               setAnchorEl(e.currentTarget);
               handleSearch(e.target.value);
             }}
             sx={{
               ml: 0.75,
               flex: 1,
               minWidth: 0,
               "& input": {
                 fontSize: "0.8125rem",
                 fontWeight: 500,
               },
             }}
           />
           
           {/* Enhanced Popper with Slide Animations */}
           <Popper
             open={Boolean(anchorEl) && (searchResults.length > 0 || loading || noDataFound)}
             anchorEl={anchorEl}
             placement="bottom-start"
             style={{ width: getLocationPopupWidth(), zIndex: 1301 }}
             transition
           >
             {({ TransitionProps }) => (
               <Fade {...TransitionProps} timeout={200}>
                 <Paper
                   sx={{
                     mt: 1,
                     maxHeight: "300px",
                     overflowY: "auto",
                     borderRadius: `${tokens.radiusLg}px`,
                     boxShadow: tokens.shadowLg,
                     border: `1px solid ${tokens.border}`,
                     backgroundColor: tokens.paper,
                   }}
                 >
                   {loading ? (
                     <LoadingSkeletons />
                   ) : searchResults.length > 0 ? (
                     searchResults.map((result, index) => (
                       <SearchResultItem
                         key={result.id}
                         result={result}
                         onClick={handleLocationSelect}
                         index={index}
                       />
                     ))
                   ) : noDataFound ? (
                     <NoResultsFound
                       searchQuery={buildLocationSearchString(searchText, selectedCountry)}
                     />
                   ) : null}
                 </Paper>
               </Fade>
             )}
           </Popper>
         </Box>
       </ClickAwayListener>

              {/* Check-in Date Picker */}
              <DatePicker
                label="Check-in"
                value={checkIn}
                onChange={(newValue) => {
                  setCheckIn(newValue);
                  if (newValue && checkOut && !checkOut.isAfter(newValue)) {
                    setCheckOut(newValue.add(1, "day"));
                  }
                }}
                minDate={dayjs()}
                format="DD-MM-YYYY"
                sx={{
                  ...datePickerSx,
                  width: { xs: "100%", md: 176 },
                  flexShrink: 0,
                  minWidth: 0,
                  alignSelf: "stretch",
                }}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: "small",
                  },
                  actionBar: {
                    actions: ["today", "clear"],
                  },
                }}
              />

              {/* Check-out Date Picker */}
              <DatePicker
                label="Check-out"
                value={checkOut}
                onChange={(newValue) => setCheckOut(newValue)}
                minDate={
                  checkIn ? checkIn.add(1, "day") : dayjs().add(1, "day")
                }
                format="DD-MM-YYYY"
                sx={{
                  ...datePickerSx,
                  width: { xs: "100%", md: 176 },
                  flexShrink: 0,
                  minWidth: 0,
                  alignSelf: "stretch",
                }}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: "small",
                  },
                  actionBar: {
                    actions: ["today", "clear"],
                  },
                }}
              />

              {/* Travelers Field */}
              <Box
                sx={{
                  flex: { xs: "1 1 auto", md: "0 0 196px" },
                  position: "relative",
                  width: { xs: "100%", md: "196px" },
                  minWidth: 0,
                  alignSelf: "stretch",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "flex-end",
                }}
              >
                <TextField
                  label="Travelers"
                  variant="outlined"
                  size="small"
                  value={`${
                    travelers.adults + travelers.children
                  } travelers , ${travelers.rooms} room${
                    travelers.rooms > 1 ? "s" : ""
                  }`}
                  onClick={() => setOpenTravelersModal(true)}
                  inputProps={{
                    readOnly: true, // This prevents editing
                  }}
                  sx={{
                    width: "100%",
                    "& .MuiOutlinedInput-root": {
                      height: "100%",
                      minHeight: fieldHeight,
                      borderRadius: `${tokens.radiusMd}px`,
                      backgroundColor: tokens.paper,
                      paddingLeft: "36px",
                      cursor: "pointer",

                      "&:hover fieldset": {
                        borderColor: tokens.borderStrong,
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: tokens.accent,
                      },
                      "& fieldset": {
                        borderColor: tokens.border,
                      },
                    },
                    "& .MuiInputLabel-root": {
                      color: tokens.textMuted,
                      fontSize: "0.8rem",
                      "&.Mui-focused": {
                        color: tokens.accent,
                      },
                      "&.MuiInputLabel-shrink": {
                        transform: "translate(14px, -9px) scale(0.75)",
                      },
                    },
                    "& .MuiOutlinedInput-input": {
                      fontSize: "0.8125rem",
                      fontWeight: 500,
                      cursor: "pointer",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      py: "10px",
                      pl: "6px",
                    },
                  }}
                >
                  <Box
                    component="div"
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      position: "absolute",
                      left: "12px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      pointerEvents: "none",
                    }}
                  >
                    <PersonOutlineIcon sx={{ color: tokens.textMuted, fontSize: "1.1rem" }} />
                  </Box>
                </TextField>
                {/* <TravelersModal /> */}
                <TravelersModal
                  isOpen={openTravelersModal}
                  handleClose={() => setOpenTravelersModal(false)}
                  onTravelersChange={handleTravelersChange}
                  initialTravelers={travelers}
                />
              </Box>

              {/* Search — stretch to row height so it matches labeled fields; larger tap target */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "stretch",
                  alignSelf: "stretch",
                  flex: { xs: "1 1 auto", md: "0 0 auto" },
                  minWidth: 0,
                  maxWidth: "100%",
                }}
              >
                <Button
                  variant="contained"
                  color="primary"
                  disableElevation
                  startIcon={<SearchIcon sx={{ fontSize: "1.15rem" }} />}
                  onClick={handleSearchHotels}
                  sx={{
                    width: { xs: "100%", md: "100%" },
                    minWidth: 0,
                    maxWidth: "100%",
                    minHeight: fieldHeight,
                    height: "100%",
                    borderRadius: `${tokens.radiusMd}px`,
                    fontSize: "0.875rem",
                    fontWeight: 700,
                    textTransform: "none",
                    letterSpacing: "0.02em",
                    px: { xs: 2.25, md: 2.25 },
                    py: 1,
                    ml: 0,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 0.75,
                    flexShrink: 0,
                    boxShadow: "0 4px 16px rgba(10, 30, 64, 0.38)",
                    transition: "box-shadow 0.2s ease, transform 0.15s ease",
                    "&:hover": {
                      boxShadow: "0 6px 22px rgba(10, 30, 64, 0.45)",
                    },
                    "& .MuiButton-startIcon": {
                      margin: 0,
                      mr: 0.25,
                    },
                  }}
                >
                  Search
                </Button>
              </Box>
            </Box>
          </Paper>
          <Box
            sx={{
              mt: 4,
              display: "flex",
              flexWrap: "wrap",
              justifyContent: "center",
              gap: { xs: 1.5, md: 2 },
            }}
          >
            {[
              "Negotiated B2B inventory",
              "Transparent pricing & taxes",
              "Built for travel desks & teams",
            ].map((label) => (
              <Box
                key={label}
                sx={{
                  px: 2,
                  py: 0.75,
                  borderRadius: `${tokens.radiusXl}px`,
                  bgcolor: "rgba(255,255,255,0.14)",
                  border: "1px solid rgba(255,255,255,0.22)",
                  color: "rgba(255,255,255,0.95)",
                  fontSize: { xs: "0.8rem", md: "0.85rem" },
                  fontWeight: 600,
                  letterSpacing: "0.02em",
                }}
              >
                {label}
              </Box>
            ))}
          </Box>
        </Container>
      </Box>

    </>
  );
};

export default SearchSection;
